import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Wallet, RefreshCw, CheckCircle2, ChevronLeft, Building2, Plus, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

function BankAccountsPanel() {
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    label: "", bank_name: "", branch: "", account_number: "",
    account_type: "checking", routing: "", is_default: false,
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/payouts/bank-accounts");
      setAccounts(r.data || []);
    } catch (err) { toast.error(errorMessage(err, "Load failed")); }
  };
  useEffect(() => { load(); }, []);

  const set = (patch) => setForm((s) => ({ ...s, ...patch }));

  const submit = async () => {
    if (!form.label || !form.bank_name || !form.account_number) {
      toast.error("Label, bank name, account number all required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/payouts/bank-accounts", form);
      toast.success("Bank account saved");
      setForm({ label: "", bank_name: "", branch: "", account_number: "", account_type: "checking", routing: "", is_default: false });
      setShowForm(false);
      await load();
    } catch (err) { toast.error(errorMessage(err, "Save failed")); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this bank account?")) return;
    try {
      await api.delete(`/admin/payouts/bank-accounts/${id}`);
      toast.success("Removed"); await load();
    } catch (err) { toast.error(errorMessage(err, "Delete failed")); }
  };

  return (
    <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-emerald-300" />
          <div className="text-emerald-200 font-display text-base">Your Bank Accounts</div>
        </div>
        <button
          data-testid="bank-toggle-form"
          onClick={() => setShowForm((s) => !s)}
          className="inline-flex items-center gap-1 text-xs text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10 rounded-md px-3 py-1.5"
        >
          <Plus className="w-3 h-3" /> Add account
        </button>
      </div>
      <div className="text-xs text-white/65 mb-3">
        Stored only for your reference — the platform never auto-debits these. The payout queue below shows what's owed; you transfer manually via NCB/Scotia online banking, then mark each ride paid.
      </div>
      <div className="space-y-2 mb-3">
        {accounts.length === 0 && <div className="text-xs text-white/40">No bank accounts yet.</div>}
        {accounts.map((a) => (
          <div key={a.id} className="flex items-center justify-between text-xs border border-white/10 rounded px-3 py-2">
            <div>
              <div className="text-white font-medium inline-flex items-center gap-1">
                {a.is_default && <Star className="w-3 h-3 text-amber-300 fill-amber-300/50" />}
                {a.label} <span className="text-white/40 font-normal">· {a.bank_name}</span>
              </div>
              <div className="text-white/45 mt-0.5">
                Acct {a.account_number}{a.branch ? ` · ${a.branch}` : ""}{a.routing ? ` · routing ${a.routing}` : ""}
              </div>
            </div>
            <button onClick={() => remove(a.id)} className="text-rose-300 hover:bg-rose-500/10 border border-rose-500/40 rounded px-2 py-1 inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        ))}
      </div>
      {showForm && (
        <div className="space-y-2 pt-3 border-t border-white/10">
          <div className="grid grid-cols-2 gap-2">
            <input
              data-testid="bank-form-label"
              type="text" value={form.label} onChange={(e) => set({ label: e.target.value })}
              placeholder="Label (e.g. Scotia Personal)"
              className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
            />
            <select
              value={form.account_type} onChange={(e) => set({ account_type: e.target.value })}
              className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
            >
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
              <option value="business">Business</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              data-testid="bank-form-name"
              type="text" value={form.bank_name} onChange={(e) => set({ bank_name: e.target.value })}
              placeholder="Bank name (Scotiabank Jamaica, NCB, JN)"
              className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
            />
            <input
              type="text" value={form.branch} onChange={(e) => set({ branch: e.target.value })}
              placeholder="Branch (optional)"
              className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              data-testid="bank-form-acct"
              type="text" value={form.account_number} onChange={(e) => set({ account_number: e.target.value })}
              placeholder="Account number"
              className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
            />
            <input
              type="text" value={form.routing} onChange={(e) => set({ routing: e.target.value })}
              placeholder="Routing / SWIFT (optional)"
              className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-white/70">
            <input type="checkbox" checked={form.is_default} onChange={(e) => set({ is_default: e.target.checked })} className="accent-emerald-500" />
            Set as default deposit account
          </label>
          <button
            data-testid="bank-form-save"
            onClick={submit} disabled={busy}
            className="bg-emerald-500 text-luxury-black hover:bg-emerald-400 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50"
          >Save</button>
        </div>
      )}
    </div>
  );
}

function DriverDetail({ email, name, onBack, onChange }) {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/payouts/drivers/${encodeURIComponent(email)}/rides`, { params: { status: "pending", days: 60 } });
      setRides(r.data || []);
    } catch (err) { toast.error(errorMessage(err, "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [email]);

  const markPaid = async (id) => {
    try {
      await api.post(`/admin/payouts/rides/${id}/mark-paid`);
      toast.success("Marked paid");
      await load();
      onChange && onChange();
    } catch (err) { toast.error(errorMessage(err, "Update failed")); }
  };

  const markAllPaid = async () => {
    if (!window.confirm(`Mark ALL ${rides.length} pending rides for ${name || email} as paid? Use this after you've completed the bank transfer.`)) return;
    try {
      const r = await api.post(`/admin/payouts/drivers/${encodeURIComponent(email)}/mark-all-paid`);
      toast.success(`Marked ${r.data?.rides_marked || 0} rides paid`);
      await load();
      onChange && onChange();
    } catch (err) { toast.error(errorMessage(err, "Bulk update failed")); }
  };

  const totalOwed = rides.reduce((s, r) => s + (r.driver_payout || 0), 0);

  return (
    <div data-testid="payout-driver-detail" className="space-y-3">
      <button onClick={onBack} className="text-xs text-gold inline-flex items-center gap-1 hover:underline">
        <ChevronLeft className="w-3 h-3" /> Back to all drivers
      </button>
      <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-white font-display text-lg">{name || email}</div>
          <div className="text-xs text-white/55">{email} · {rides.length} pending rides</div>
        </div>
        <div className="text-right">
          <div className="text-2xl text-emerald-300 font-display">JMD {totalOwed.toLocaleString()}</div>
          <button
            data-testid="payout-mark-all"
            onClick={markAllPaid}
            disabled={rides.length === 0}
            className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10 rounded-md px-3 py-1.5 disabled:opacity-50"
          >
            <CheckCircle2 className="w-3 h-3" /> Mark all paid
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-center text-white/50 py-8">Loading…</div>
      ) : (
        <div className="space-y-2">
          {rides.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 text-xs border border-white/10 rounded px-3 py-2 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-white">{r.passenger_name || "Anonymous"} · <span className="text-white/55">{r.type}</span></div>
                <div className="text-white/45 truncate">{r.pickup_address} → {r.dropoff_address}</div>
                <div className="text-white/35 text-[10px]">Completed {new Date(r.completed_at).toLocaleString()}</div>
              </div>
              <div className="text-right">
                <div className="text-emerald-300 font-mono">JMD {(r.driver_payout || 0).toLocaleString()}</div>
                <button
                  data-testid={`payout-mark-${r.id}`}
                  onClick={() => markPaid(r.id)}
                  className="mt-1 text-[10px] text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10 rounded px-2 py-0.5"
                >Mark paid</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPayoutsTab() {
  const [drivers, setDrivers] = useState([]);
  const [totalOwed, setTotalOwed] = useState(0);
  const [activeDriver, setActiveDriver] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/payouts/drivers", { params: { status: "pending", days: 30 } });
      setDrivers(r.data?.drivers || []);
      setTotalOwed(r.data?.total_owed || 0);
    } catch (err) { toast.error(errorMessage(err, "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (activeDriver) {
    return (
      <DriverDetail
        email={activeDriver.email} name={activeDriver.name}
        onBack={() => { setActiveDriver(null); load(); }}
        onChange={load}
      />
    );
  }

  return (
    <div data-testid="admin-payouts-tab" className="space-y-4">
      <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Wallet className="w-5 h-5 text-emerald-300 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-emerald-200 font-display text-base">Driver Payouts</div>
            <div className="text-xs text-white/65 mt-1">
              Self-managed. Use NCB/Scotia online banking to transfer; tap "mark paid" once done. Last 30 days.
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl text-emerald-300 font-display">JMD {totalOwed.toLocaleString()}</div>
          <div className="text-[10px] text-white/45 uppercase tracking-wider">Total owed</div>
        </div>
        <button onClick={load} className="text-white/50 hover:text-emerald-300 p-1.5">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <BankAccountsPanel />

      <div className="text-xs text-white/55 uppercase tracking-wider">Drivers awaiting payout</div>
      {loading && <div className="text-center text-white/50 py-8">Loading…</div>}
      {!loading && drivers.length === 0 && (
        <div className="text-center text-white/50 py-12 border border-dashed border-white/10 rounded-md">
          Nothing owed — every driver is paid up. 🎯
        </div>
      )}
      <div className="space-y-2">
        {drivers.map((d) => (
          <motion.button
            key={d.driver_email} data-testid={`payout-driver-${d.driver_email}`}
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            onClick={() => setActiveDriver({ email: d.driver_email, name: d.driver_name })}
            className="w-full text-left rounded-md border border-white/10 hover:border-emerald-500/40 bg-black/30 p-3 flex items-center justify-between gap-3 transition-colors"
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">{d.driver_name || d.driver_email}</div>
              <div className="text-xs text-white/50">{d.driver_email} · {d.rides} rides · last {new Date(d.last_completed).toLocaleDateString()}</div>
            </div>
            <div className="text-emerald-300 font-mono text-sm">JMD {(d.owed || 0).toLocaleString()}</div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
